import Phaser from 'phaser';
import type { Tile } from '@rummikub/engine';

export const TILE_W = 64;
export const TILE_H = 88;

// 注意：engine 內部仍以 'orange' 為 key，視覺上呈現為黃色
const COLOR_HEX: Record<string, number> = {
  black: 0x111827,
  red: 0xdc2626,
  blue: 0x2563eb,
  orange: 0xca8a04, // yellow-600 — 在白底上有足夠對比
};
const COLOR_RGBA: Record<string, string> = {
  black: '#111827',
  red: '#dc2626',
  blue: '#2563eb',
  orange: '#ca8a04',
};

/** 產生一張牌的高質感貼圖（白底 + 陰影 + 大字 + Rummikub 浮水印）。 */
export function ensureTileTexture(scene: Phaser.Scene, tile: Tile): string {
  const key = `tile-${tile.isJoker ? 'joker' : `${tile.color}-${tile.number}`}`;
  if (scene.textures.exists(key)) return key;

  const PAD = 6;
  const CANVAS_W = TILE_W + PAD * 2;
  const CANVAS_H = TILE_H + PAD * 2;

  const canvas = document.createElement('canvas');
  canvas.width = CANVAS_W;
  canvas.height = CANVAS_H;
  const ctx = canvas.getContext('2d')!;

  const x = PAD;
  const y = PAD;
  const w = TILE_W;
  const h = TILE_H;
  const r = 8;

  // 陰影
  ctx.save();
  ctx.shadowColor = 'rgba(0,0,0,0.35)';
  ctx.shadowBlur = 6;
  ctx.shadowOffsetY = 3;
  ctx.fillStyle = '#ffffff';
  roundRect(ctx, x, y, w, h, r);
  ctx.fill();
  ctx.restore();

  // 底色（淡灰漸層）
  const grad = ctx.createLinearGradient(0, y, 0, y + h);
  grad.addColorStop(0, '#ffffff');
  grad.addColorStop(0.5, '#fafafa');
  grad.addColorStop(1, '#e5e5e5');
  ctx.fillStyle = grad;
  roundRect(ctx, x, y, w, h, r);
  ctx.fill();

  // 上方高光
  const hi = ctx.createLinearGradient(0, y, 0, y + 12);
  hi.addColorStop(0, 'rgba(255,255,255,0.95)');
  hi.addColorStop(1, 'rgba(255,255,255,0)');
  ctx.fillStyle = hi;
  roundRect(ctx, x + 2, y + 2, w - 4, 12, 6);
  ctx.fill();

  // 文字（百搭用紅藍漸層）
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  const cx = x + w / 2;
  const cy = y + h / 2 - 2;

  if (tile.isJoker) {
    // 笑臉小丑符號（簡化）
    ctx.font = 'bold 36px "Inter", "Noto Sans TC", Arial';
    const jg = ctx.createLinearGradient(cx - 18, cy - 18, cx + 18, cy + 18);
    jg.addColorStop(0, '#dc2626');
    jg.addColorStop(0.5, '#7c3aed');
    jg.addColorStop(1, '#2563eb');
    ctx.fillStyle = jg;
    drawSmiley(ctx, cx, cy, 18);
  } else if (tile.number != null && tile.color) {
    ctx.font = 'bold 44px "Inter", "Noto Sans TC", Arial';
    ctx.fillStyle = COLOR_RGBA[tile.color] ?? '#111827';
    ctx.fillText(String(tile.number), cx, cy);
  }

  // 底部品牌字（小、淺）
  ctx.font = '7px "Inter", Arial';
  ctx.fillStyle = 'rgba(120, 53, 15, 0.55)';
  ctx.fillText('Rummikub', cx, y + h - 8);

  scene.textures.addCanvas(key, canvas);
  return key;
}

export function colorHexOf(tile: Tile): number {
  if (tile.isJoker) return 0x7c3aed;
  return COLOR_HEX[tile.color ?? 'black'] ?? 0x111827;
}

function roundRect(ctx: CanvasRenderingContext2D, x: number, y: number, w: number, h: number, r: number) {
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.lineTo(x + w - r, y);
  ctx.quadraticCurveTo(x + w, y, x + w, y + r);
  ctx.lineTo(x + w, y + h - r);
  ctx.quadraticCurveTo(x + w, y + h, x + w - r, y + h);
  ctx.lineTo(x + r, y + h);
  ctx.quadraticCurveTo(x, y + h, x, y + h - r);
  ctx.lineTo(x, y + r);
  ctx.quadraticCurveTo(x, y, x + r, y);
  ctx.closePath();
}

function drawSmiley(ctx: CanvasRenderingContext2D, cx: number, cy: number, r: number) {
  // 紅色臉
  ctx.fillStyle = '#dc2626';
  ctx.beginPath();
  ctx.arc(cx, cy, r, 0, Math.PI * 2);
  ctx.fill();
  // 帽子（藍色三角）
  ctx.fillStyle = '#2563eb';
  ctx.beginPath();
  ctx.moveTo(cx - r * 0.9, cy - r * 0.4);
  ctx.lineTo(cx + r * 0.9, cy - r * 0.4);
  ctx.lineTo(cx, cy - r * 1.4);
  ctx.closePath();
  ctx.fill();
  // 眼睛
  ctx.fillStyle = '#ffffff';
  ctx.beginPath();
  ctx.arc(cx - r * 0.35, cy + r * 0.05, r * 0.18, 0, Math.PI * 2);
  ctx.arc(cx + r * 0.35, cy + r * 0.05, r * 0.18, 0, Math.PI * 2);
  ctx.fill();
  // 笑嘴
  ctx.strokeStyle = '#ffffff';
  ctx.lineWidth = 2.5;
  ctx.lineCap = 'round';
  ctx.beginPath();
  ctx.arc(cx, cy + r * 0.2, r * 0.45, 0, Math.PI);
  ctx.stroke();
}

export const PADDED_W = TILE_W + 12;
export const PADDED_H = TILE_H + 12;
